import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Avatar, AvatarFallback } from "@shared/ui/avatar";
import { AlertCircle, CreditCard, Filter, Eye, X, ChevronDown, CheckCircle2, XCircle, Download, FileText, PowerOff, Power, Wrench, MessageSquare, ArrowRightLeft } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableAction } from "@shared/types/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@shared/ui/dropdown-menu";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useAllAccounts, useAccountStatusChoices } from "../../../shared/hook";
import { useUtilityServices } from "../../../guest-portal/hooks";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { useTranslation } from 'react-i18next';

const getColumns = (t: any): TableColumn<any>[] => [
  {
    key: "account",
    header: t("Account"),
    render: (_, row) => (
      <div className="flex items-center gap-3">
        <Avatar className="w-8 h-8 bg-blue-50 text-blue-700">
          <AvatarFallback className="text-xs font-semibold">{row.accountAvatar}</AvatarFallback>
        </Avatar>
        <div>
          <p className="font-semibold text-sm text-gray-900">{row.accountName}</p>
          <p className="text-xs text-muted-foreground">{row.accountSubtitle}</p>
        </div>
      </div>
    )
  },
  {
    key: "consumerNo",
    header: t("Consumer No."),
    render: (val) => <span className="font-medium text-sm text-gray-600">{val}</span>
  },
  {
    key: "type",
    header: t("Type"),
    render: (val) => {
      let bg = "bg-blue-100 text-blue-700";
      if (val === "Tenant") bg = "bg-teal-100 text-teal-700";
      if (val === "Commercial") bg = "bg-purple-100 text-purple-700";
      return <Badge variant="secondary" className={`${bg} border-0 rounded-full px-2 py-0.5 text-xs font-medium`}>{val}</Badge>;
    }
  },
  {
    key: "address",
    header: t("Service Address"),
    render: (val) => <span className="text-xs text-muted-foreground whitespace-pre-wrap leading-tight block">{val}</span>
  },
  {
    key: "services",
    header: t("Services"),
    render: (val: string[]) => (
      <div className="flex gap-1 flex-wrap">
        {val.map((s:string) => (
          <Badge key={s} variant="outline" >
            {s}
          </Badge>
        ))}
      </div>
    )
  },
  {
    key: "balanceDue",
    header: t("Balance Due"),
    render: (val) => {
      const isPaid = val.includes("Paid");
      return (
        <span className={`font-bold text-sm ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
          {val}
        </span>
      );
    }
  },
  {
    key: "nextDue",
    header: t("Next Due"),
    render: (val) => <span className="text-sm text-gray-600">{val}</span>
  },
  {
    key: "autopay",
    header: t("Autopay"),
    render: (val) => (
      <span className="text-xs text-gray-500 flex items-center gap-1.5">
        {val ? <span className="w-3.5 h-3.5 text-green-500 border border-green-500 rounded-full inline-flex items-center justify-center text-[8px] font-bold">✓</span> : <span className="w-3.5 h-3.5 border rounded-full inline-block border-gray-300"></span>}
        {val ? "On" : "Off"}
      </span>
    )
  },
  {
    key: "status",
    header: t("Status"),
    render: (val) => (
      <span className="text-xs font-medium text-green-600 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block"></span>
        {val}
      </span>
    )
  }
];

export default function AllAccountsTab() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchData, setSearchData] = useState("");
  const limit = 50;
  const currency = useCurrency();
  const { t } = useTranslation();
  const [appliedFilters, setAppliedFilters] = useState({
    status: "all",
    utilityService: "all"
  });

  const { data: allAccounts, isLoading } = useAllAccounts({
    page: page,
    limit: limit,
    active_consumers: 1,
    remote_utility_id: getRemoteUtilityId(),
    is_sub_account: 1,
    consumer_id: getLoginDataFromStorage().consumerId,
    searchData: searchData,
    status: appliedFilters.status,
    utilityService: appliedFilters.utilityService,
  });

  const tableData = useMemo(() => {
    if (!allAccounts?.results) return [];
    return allAccounts.results.map((item: any) => ({
      id: item.id.toString(),
      accountAvatar: `${item.firstName?.charAt(0) || ''}${item.lastName?.charAt(0) || ''}`.toUpperCase(),
      accountName: `${item.firstName} ${item.lastName}`,
      accountSubtitle: item.ownershipDisplay,
      consumerNo: item.consumerNo,
      type: item.ownershipDisplay,
      address: `${item.territoryData?.service?.address || ''}\n${item.territoryData?.service?.unit || ''}`.trim(),
      services: item.additionalData?.nonMeterServices || [],
      balanceDue: item?.outstandingBalance ? `${currency} ${item?.outstandingBalance}` : "Paid ✓",
      nextDue: "N/A",
      autopay: item.additionalData?.paymentPlanMode === "autopay",
      status: item.statusDisplay,
      services:item.utilityServices
    }));
  }, [allAccounts]);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const form = useForm({
    defaultValues: {
      status: "all",
      utilityService: "all"
    }
  });

  const { data: utilityData } = useUtilityServices({ remote_utility_id: String(getRemoteUtilityId()) });

  const utilityServiceOptions = useMemo(() => {
    const defaultOptions = [{ label: "All", value: "all" }];
    if (utilityData) {
      const apiOptions = utilityData
        .filter((utility: any) => utility.isActive === true)
        .map((utility: any) => ({
          label: utility.name,
          value: utility.name,
        }));
      return [...defaultOptions, ...apiOptions];
    }
    return defaultOptions;
  }, [utilityData]);

  const { data: statusData } = useAccountStatusChoices();

  const statusOptions = useMemo(() => {
    const defaultOptions = [{ label: "All", value: "all" }];
    if (statusData?.result) {
      const apiOptions = statusData.result.map((status: any) => ({
        label: status.value, // value is "Active", key is "active" usually, we will use key or value? Let's check typical choices api: key/value.
        value: status.value,
      }));
      return [...defaultOptions, ...apiOptions];
    }
    return defaultOptions;
  }, [statusData]);

  const filterFields: any[] = useMemo(() => [
    {
      name: "status",
      label: "Status",
      type: "select",
      options: statusOptions
    },
    {
      name: "utilityService",
      label: "Utility Service",
      type: "select",
      options: utilityServiceOptions
    }
  ], [t, utilityServiceOptions, statusOptions]);

  const totalDue = selectedRows.reduce((acc, id) => {
    const row = tableData.find((r: any) => r.id === id);
    if (row) {
      return acc + (Number(row.outstandingBalance) || 0);
    }
    return acc;
  }, 0);

  const tableActions: TableAction<any>[] = useMemo(() => [
    {
      label: t("View"),
      icon: <Eye className="w-4 h-4" />,
      onClick: (row) => navigate(`/dashboard/all-accounts/${row.id}`),
      variant: "ghost"
    },
    {
      label: t("Pay"),
      icon: <CreditCard className="w-4 h-4" />,
      onClick: (row) => navigate(`/dashboard/billing/pay?type=bills&isAllAccounts=true&consumer_no=${row.consumerNo}`),
      variant: "ghost",
      hidden: (row) => {
        const balance = Number(row.outstandingBalance) || 0;
        return balance <= 0;
      }
    },
    {
      label: t("Raise Complaint"),
      icon: <MessageSquare className="w-4 h-4" />,
      onClick: (row) => {
        const params = new URLSearchParams({
          consumerId: row.id.toString(),
          isSubAccount: 'true'
        });
        navigate(`/dashboard/requests/create/complaint?${params.toString()}`);
      },
      variant: "ghost"
    },
    {
      label: t("Service Request"),
      icon: <Wrench className="w-4 h-4" />,
      onClick: (row) => {
        const params = new URLSearchParams({
          consumerId: row.id.toString(),
          isSubAccount: 'true'
        });
        navigate(`/dashboard/requests/create/service?${params.toString()}`);
      },
      variant: "ghost"
    },
    {
      label: t("Request Disconnection"),
      icon: <PowerOff className="w-4 h-4" />,
      onClick: (row) => {
        const params = new URLSearchParams({
          consumerId: row.id.toString(),
          isSubAccount: 'true'
        });
        navigate(`/dashboard/requests/create/disconnect?${params.toString()}`);
      },
      variant: "ghost"
    },
    {
      label: t("Request Reconnection"),
      icon: <Power className="w-4 h-4" />,
      onClick: (row) => {
        const params = new URLSearchParams({
          consumerId: row.id.toString(),
          isSubAccount: 'true'
        });
        navigate(`/dashboard/requests/create/reconnect?${params.toString()}`);
      },
      variant: "ghost"
    },
    {
      label: t("Request Transfer"),
      icon: <ArrowRightLeft className="w-4 h-4" />,
      onClick: (row) => {
        const params = new URLSearchParams({
          consumerId: row.id.toString(),
          isSubAccount: 'true'
        });
        navigate(`/dashboard/requests/create/transfer?${params.toString()}`);
      },
      variant: "ghost"
    }
  ], [t, navigate]);

  const columns = useMemo(() => getColumns(t), [t]);

  return (
    <div className="space-y-6 mt-6">
      {/* Alert Banner */}
      <div className="bg-red-50/80 border border-red-100 rounded-lg p-3 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <span className="font-semibold text-red-700 text-sm">{t("{{count}} accounts have outstanding balances", { count: 0 })}</span>
            <span className="text-xs text-red-600/80">NA - {t("Total")}: <span className="font-semibold">NA</span></span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="link" className="text-red-700 text-sm font-semibold hover:no-underline p-0 h-auto" onClick={()=>navigate('/dashboard/billing')}>
            {t("View All Bills →")}
          </Button>
          <Button variant="destructive" onClick={() => {
            const allSubAccountConsumerNos = tableData.map(row => row.consumerNo).join(',');
            navigate(`/dashboard/billing/pay?type=bills&isAllAccounts=true&consumer_no=${allSubAccountConsumerNos}`);
          }}>
            <CreditCard className="w-4 h-4" /> {t("Pay All Outstanding")}
          </Button>
        </div>
      </div>

      <div>
        {selectedRows.length > 1 && (
          <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col md:flex-row items-center justify-between shadow-sm mb-4 gap-4">
            <div className="flex items-center gap-4 w-full md:w-auto pl-2">
              <span className="font-semibold text-sm text-gray-900">{t("{{count}} accounts selected", { count: selectedRows.length })}</span>
              {totalDue > 0 && (
                <Badge className="bg-red-50 text-red-600 hover:bg-red-50 border-red-100 rounded-full px-2.5 py-0.5 text-xs font-medium">
                  {t("{{amount}} due", { amount: `$${totalDue}` })}
                </Badge>
              )}
              <button onClick={() => setSelectedRows([])} className="text-gray-400 hover:text-gray-600 transition-colors ml-2">
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end pr-1">              
              <Button 
                variant="outline" 
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 h-9 shadow-sm"
                onClick={() => navigate('/dashboard/requests/create/service?multiAccount=true', { state: { accountIds: selectedRows } })}
              >
                <Wrench className="w-4 h-4 mr-2" /> {t("Service Request")}
              </Button>
              <Button 
                variant="outline" 
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 h-9 shadow-sm"
                onClick={() => navigate('/dashboard/requests/create/complaint?multiAccount=true', { state: { accountIds: selectedRows } })}
              >
                <MessageSquare className="w-4 h-4 mr-2" /> {t("Raise Complaint")}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 h-9 font-medium shadow-sm">
                    {t("More Actions")} <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuItem 
                    className="te xt-red-600 focus:text-red-600"
                    onClick={() => navigate('/dashboard/requests/create/disconnect?multiAccount=true', { state: { accountIds: selectedRows } })}
                  >
                    <PowerOff className="w-4 h-4 mr-3 text-red-500" /> {t("Request Disconnection")}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-green-600 focus:text-green-600"
                    onClick={() => navigate('/dashboard/requests/create/reconnect?multiAccount=true', { state: { accountIds: selectedRows } })}
                  >
                    <Power className="w-4 h-4 mr-3 text-green-500" /> {t("Request Reconnection")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg">
           <GenericTable
            data={tableData}
            loading={isLoading}
            pagination={{
              currentPage: page,
              totalPages: Math.ceil((allAccounts?.count || 0) / limit) || 1,
              totalItems: allAccounts?.count || 0,
              startItem: allAccounts?.count === 0 ? 0 : (page - 1) * limit + 1,
              endItem: Math.min(page * limit, allAccounts?.count || 0)
            }}
            onPageChange={(newPage) => setPage(newPage)}
            columns={columns}
            rowKey="id"
            selectable={true}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            searchPlaceholder={t("Search by name, consumer number, address...")}
            onSearch={(val) => {
              setSearchData(val);
              setPage(1);
            }}
            actions={tableActions}
            actionsType="dropdown"
            filterPanel={
              <DynamicForm 
                form={form} 
                fields={filterFields} 
                gridColumns={2} 
                config={{ 
                  showSubmitButton: true, 
                  submitLabel: "Apply Filters", 
                  showResetButton: true 
                }}
                onSubmit={(data: any) => {
                  setAppliedFilters({
                    status: data.status || "all",
                    utilityService: data.utilityService || "all"
                  });
                  setPage(1);
                }}
                onReset={() => {
                  setAppliedFilters({ status: "all", utilityService: "all" });
                  setPage(1);
                  form.reset({ status: "all", utilityService: "all" });
                }}
              />
            }
          />
        </div>
      </div>
    </div>
  );
}
